import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { ZodError } from 'zod';
import { fromError } from 'zod-validation-error';

import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';

// Fragments
const DefaultFallback = () => <Navigate to="/" />;

// Exports
export type RouteErrorBoundaryFallbackFn = (options: {
  error: unknown;
  message: string;
  showErrorToast: (toastText: string) => void;
}) => React.ReactElement;
type Props = {
  children: React.ReactNode;
  fallback?: React.ReactElement | RouteErrorBoundaryFallbackFn;
  resetAfterCatch?: boolean;
};
export const RouteErrorBoundary = ({
  children,
  fallback = <DefaultFallback />,
  resetAfterCatch = true,
}: Props) => {
  const toast = useToast();
  const resolvedFallback =
    typeof fallback === 'function' ? fallback : () => fallback;

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        // Let React get through a render cycle (for the fallback UI) before resetting the boundary
        if (resetAfterCatch) setTimeout(() => resetError(), 0);

        let message: string = 'Something went wrong';
        if (error instanceof ZodError) {
          message = fromError(error).message.replace('Validation error: ', '');
        } else if (error instanceof Error) {
          message = error.message;
        }

        return resolvedFallback({
          error,
          message,
          showErrorToast: (toastText) => {
            if (!toast.isActive(errorToastId)) {
              setTimeout(() => {
                toast({ ...errorToastConfig, title: toastText });
              }, 0);
            }
          },
        });
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
